import React, {useEffect, useState} from 'react'
import InformationModal from '../../../components/shared/informationmodal/InformationModal.js';
import {useTranslation} from 'react-i18next';
import AddUserModal from './addusermodal/AddUserModal.js';
import ApiUserModal from './apiusermodal/ApiUserModal.js';
import {useDispatch, useSelector} from 'react-redux';
import EditUserModal from './editusermodal/EditUserModal.js';
import {
    AddUser,
    createApiUserAction,
    editApiUserAction,
    deleteApiUserAction,
    deleteUserAndGroups,
    editUserAndGroups,
    sendInvitation
} from '../../../helpers/reduxstore/reducers/userAdministrationReducer.js';
import useUserRole from '../../../helpers/hooks/useUserRole.js';
import UserTable from './usertable/UserTable'
import { clone, set } from 'lodash';

/** Constructs an overview of users who have been invited to the platform */
export default function UserManagement() {
    const {t} = useTranslation(['userpage', 'common'])
    const dispatch = useDispatch()
    const [deleteUserModalVisibility, setDeleteUserModalVisibility] = useState(false)
    const [privilegesModalVisibility, setPrivilegesModalVisibility] = useState(false)
    const [editModalVisibility, setEditModalVisibility] = useState(false)
    const [apiUserModalVisibility, setApiUserModalVisibility] = useState(false)
    const [deleteApiUserModalVisibility, setDeleteApiUserModalVisibility] = useState(false)
    const [editUserGroups, setEditUserGroups] = useState([])
    const [editUserRole, setEditUserRole] = useState(null)
    const [editUserLanguage, setEditUserLanguage] = useState(null)
    const [editUserId, setEditUserId] = useState(null)

    const [addUserModalVisibility, setAddUserModalVisibility] = useState(false)
    const [deleteUserId, setDeleteUserId] = useState("")
    const [apiUserId, setApiUserId] = useState(null)
    const [apiUserData, setApiUserData] = useState([])
    const {administeredUsers} = useSelector(state => state.userAdministration)
    const userRole = useUserRole();

    const addUser = (email, role, selectedGroups, preferredLanguage) => {
        setAddUserModalVisibility(!addUserModalVisibility)
        dispatch(AddUser(selectedGroups.map(g => g.id), email.trim(), role, preferredLanguage))
    }

    /** Open edit user modal
     * @param {object} user to edit
     */
    const openEditModal = (user) => {  
        setEditUserGroups([])
        setEditUserRole(null)
        setEditUserLanguage(null)

        const selectedAdministeredUser = administeredUsers.find(u => u.userId === user.userId)
        if (selectedAdministeredUser) {
            setEditUserId(user.userId)
            setEditUserGroups(selectedAdministeredUser.groups)
            setEditUserRole(selectedAdministeredUser.userData.appRole)
            setEditUserLanguage(selectedAdministeredUser.userData.preferredLanguage)
        }

        setEditModalVisibility(true)
    }
    const editUser = (selectedGroups, role, preferredLanguage) => {
        dispatch(editUserAndGroups(selectedGroups.map(g => g.id), editUserId, role, preferredLanguage))
        setEditModalVisibility(false)
    }
    const openDeleteModal = (userId) => {
        setDeleteUserId(userId)
        setDeleteUserModalVisibility(true)
    }
    const deleteUser = () => {
        dispatch(deleteUserAndGroups(deleteUserId))
        setDeleteUserModalVisibility(false)
    }

    const openApiUserModal = (userId) => {
        setApiUserId(userId)
        const selectedAdministeredUser = administeredUsers.find(u => u.userId === userId)
        if (selectedAdministeredUser) {
            setApiUserData(selectedAdministeredUser.apiUserData)
        }
        setApiUserModalVisibility(true)
    }

    const createApiUser = async (readOnly) => {
        const updatedApiUserData = await dispatch(createApiUserAction(apiUserId, readOnly));
        setApiUserData(updatedApiUserData); // Opdater `apiUserData` med de nye data
    };

    const editApiUser = async (readOnly, newSecret) => {
       const updateApiUserData = await dispatch(editApiUserAction(apiUserData.id, readOnly, newSecret))
       setApiUserData(updateApiUserData); // Opdater `apiUserData` med de nye data
    }

    const openDeleteApiUserModal = () => {
        setDeleteApiUserModalVisibility(true)
    }

    const deleteApiUser = () => {
        dispatch(deleteApiUserAction(apiUserData.id, apiUserData.userId))
        setApiUserData(null); 
        setDeleteApiUserModalVisibility(false)
    }

    useEffect(() => {
        if (apiUserModalVisibility && apiUserData) {
            // Refresh modalens data, når `apiUserData` ændres
            setApiUserData(apiUserData);
        }
    }, [apiUserData, apiUserModalVisibility]);

    return (
        <>
            <InformationModal show={deleteUserModalVisibility} title={t('userpage:user_management.delete_modal.title')}
                              body={t('userpage:user_management.delete_modal.body')}
                              accept={() => deleteUser()} dismiss={() => setDeleteUserModalVisibility(false)}/>

            <InformationModal show={deleteApiUserModalVisibility} title={t('userpage:user_management.delete_api_modal.title')}
                              body={t('userpage:user_management.delete_api_modal.body')}
                              accept={() => deleteApiUser()} dismiss={() => setDeleteApiUserModalVisibility(false)}/>                  

            <InformationModal show={privilegesModalVisibility} title={t('userpage:user_management.priv_modal.title')}
                              body={t('userpage:user_management.priv_modal.body')}
                              accept={() => setPrivilegesModalVisibility(!privilegesModalVisibility)}
                              dismiss={() => setPrivilegesModalVisibility(!privilegesModalVisibility)}/>
            <AddUserModal creatorRoleList={userRole} addUser={addUser}
                          setVisibility={() => setAddUserModalVisibility(!addUserModalVisibility)}
                          show={addUserModalVisibility}/>
            <ApiUserModal show={apiUserModalVisibility} apiUserData={apiUserData} setVisibility={() => setApiUserModalVisibility(!apiUserModalVisibility)} createApiUser={createApiUser}
              editApiUser={editApiUser} openDeleteApiUserModal={openDeleteApiUserModal} />
            <EditUserModal editorRole={userRole}
                           setVisibility={() => setEditModalVisibility(!editModalVisibility)} show={editModalVisibility}
                           editUser={editUser} userData={editUserGroups} language={editUserLanguage}
                           userRole={editUserRole}/>
            <div className='boxstyling h-100 px-4 d-flex flex-column mt-xs-2'>
                <div className='pt-4'>
                    <div style={{fontSize: '1.5rem'}} className='font-weight-500'>{t('userpage:user_management.info_title')}</div>
                    <div>{t('userpage:user_management.info_body')}</div>
                </div>

                {/*<TopInformation className='pt-4' title={t('userpage:user_management.info_title')}*/}
                {/*                subTitle={t('userpage:user_management.info_body')}/>*/}

                <UserTable showAddUserModal={() => setAddUserModalVisibility(!addUserModalVisibility)}
                           administeredUsers={administeredUsers} openEditModal={openEditModal}
                           openDeleteModal={openDeleteModal}
                           openApiUserModal={openApiUserModal}
                           openPrivilegesmodal={() => setPrivilegesModalVisibility(!privilegesModalVisibility)}
                           sendReinvite={(mail) => dispatch(sendInvitation(mail))}/>
            </div>
        </>
    )
}
